import { Children, useEffect, type HTMLAttributes, type ReactNode } from "react";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcHeading, type NcHeadingProps } from "./nc-heading";
import { NcLoadingIndicator } from "./nc-loading-indicator";

const ncPageVariants = cv("relative w-full h-full overflow-auto flex bg-light", {
  variants: {
    variant: {
      default: "flex-col",
      subNavigation: "flex-col sm:flex-row",
    },
    isLoading: {
      true: "content-center justify-center py-8",
      false: "",
    },
  },
  defaultVariants: {
    variant: "default",
    isLoading: false,
  },
});

const ncPageWrapperVariants = cv("grow flex flex-col w-full px-8 mx-auto", {
  variants: {
    variant: {
      default: "max-w-[100rem]",
      fullWidth: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const ncPageHeaderVariants = cv("flex items-center gap-2 bg-light pt-6 pb-4 border-b border-ui", {
  variants: {
    variant: {
      default: "",
      sticky:
        "sticky top-0 z-1 animate-overflow-shadow-header [animation-timeline:scroll(nearest)]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const ncPageBodyVariants = cv("grid content-start gap-8 py-6 print:max-h-none", {
  variants: {
    variant: {
      default: "",
      fullHeight: "grow",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const ncPageFooterVariants = cv("flex items-center gap-2 py-4 bg-light border-t border-ui", {
  variants: {
    variant: {
      default: "",
      sticky:
        "sticky bottom-0 z-1 animate-overflow-shadow-footer [animation-timeline:scroll(nearest)]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface NcPageProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof ncPageVariants> {
  children?: ReactNode;
  error?: boolean | string;
  empty?: boolean | string;
}

type NcPageState = "LOADING" | "LOADED" | "EMPTY" | "ERROR";

function getPageState({ isLoading, empty, error }: Omit<NcPageProps, "children">): NcPageState {
  if (isLoading) return "LOADING";
  if (empty) return "EMPTY";
  if (error) return "ERROR";
  return "LOADED";
}

const NcPageError = ({ error }: { error: NcPageProps["error"] }) => {
  const { t } = useI18n();
  const errorText = typeof error === "string" ? error : t("error_default");
  return (
    <NcFormattedMessage variant="danger" className="p-3">
      {errorText}
    </NcFormattedMessage>
  );
};

const NcPageEmpty = ({ empty }: { empty: NcPageProps["empty"] }) => {
  const { t } = useI18n();
  const emptyText = typeof empty === "string" ? empty : t("empty_default");
  return (
    <NcFormattedMessage variant="secondary" className="p-3">
      {emptyText}
    </NcFormattedMessage>
  );
};

export const NcPage = ({
  children,
  empty,
  error,
  isLoading,
  variant,
  className,
  ...props
}: NcPageProps) => {
  const state = getPageState({ isLoading, empty, error });
  return (
    <div
      data-nc="NcPage"
      className={cn(ncPageVariants({ variant, isLoading: !!isLoading }), className)}
      {...props}
    >
      {state === "LOADING" && <NcLoadingIndicator variant="logo" />}
      {state === "ERROR" && <NcPageError error={error} />}
      {state === "EMPTY" && <NcPageEmpty empty={empty} />}
      {state === "LOADED" && <>{children}</>}
    </div>
  );
};

interface NcPageWrapperProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof ncPageWrapperVariants> {}

const NcPageWrapper = ({ variant = "default", className, ...props }: NcPageWrapperProps) => (
  <div
    data-nc="NcPage.Wrapper"
    className={cn(ncPageWrapperVariants({ variant }), className)}
    {...props}
  />
);

interface NcPageHeaderProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof ncPageHeaderVariants> {}

const NcPageHeader = ({ variant = "default", className, ...props }: NcPageHeaderProps) => (
  <div
    data-nc="NcPage.Header"
    className={cn(ncPageHeaderVariants({ variant }), className)}
    {...props}
  />
);

interface NcPageBodyProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof ncPageBodyVariants> {}

const NcPageBody = ({ variant = "default", className, ...props }: NcPageBodyProps) => (
  <div
    data-nc="NcPage.Body"
    className={cn(ncPageBodyVariants({ variant }), className)}
    {...props}
  />
);

interface NcPageFooterProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof ncPageFooterVariants> {}

const NcPageFooter = ({ className, variant, ...props }: NcPageFooterProps) => (
  <div
    data-nc="NcPage.Footer"
    className={cn(ncPageFooterVariants({ variant }), className)}
    {...props}
  />
);

const getComponentText = (children: ReactNode) =>
  Children.toArray(children)
    .filter(child => typeof child === "string")
    .join(" ") || "Noted";

interface NcPageHeadingProps extends Omit<NcHeadingProps, "level" | "styleAs"> {
  level?: NcHeadingProps["level"];
  styleAs?: NcHeadingProps["styleAs"];
}
const NcPageHeading = ({ children, level = 1, styleAs = 1, ...props }: NcPageHeadingProps) => {
  useEffect(() => {
    if (level === 1) {
      document.title = getComponentText(children);
    }
  }, [children]);

  return (
    <NcHeading level={level} styleAs={styleAs} {...props}>
      {children}
    </NcHeading>
  );
};

NcPage.Wrapper = NcPageWrapper;
NcPage.Header = NcPageHeader;
NcPage.Heading = NcPageHeading;
NcPage.Body = NcPageBody;
NcPage.Footer = NcPageFooter;
